import {
  ArrowsAltOutlined,
  DownOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import { Button, Card, Dropdown, Popover, Radio, Space, Spin } from "antd";
import moment from "moment";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as ReTooltip,
  XAxis,
  YAxis,
} from "recharts";

import { MemberType, TimePeriod } from "./MembersOverTimeCard";
import {
  NewMembersDay,
  NewMembersMonth,
  NewMembersWeek,
} from "./useNewMembers";
import {
  TotalMembersDay,
  TotalMembersMonth,
  TotalMembersWeek,
} from "./useTotalMembers";

const { Meta } = Card;

type ChartProps = {
  platform: string; // "slack" or "discord"
  axisDisplay: () => TimePeriod; // callback to show the correct x-axis dates when switching time intervals
  totalMembersDay?: TotalMembersDay[]; // list total Slack members grouped by day
  totalMembersWeek?: TotalMembersWeek[]; // list total Slack members grouped by week
  totalMembersMonth?: TotalMembersMonth[]; // list total Slack members grouped by month
  newMembersDay?: NewMembersDay[]; // list new Slack members grouped by day
  newMembersWeek?: NewMembersWeek[]; // list new Slack members grouped by week
  newMembersMonth?: NewMembersMonth[]; // list new Slack members grouped by month
  handleMemberType: (e: any) => void; // callback to update the member type in the parent component
  menu: JSX.Element; // the dropdown menu to show
  popupContent: (text: string) => JSX.Element; // the text for the info button
  expandMembersOverTime: boolean; // the expanded state of the card in the parent component
  setExpandMembersOverTime: React.Dispatch<React.SetStateAction<boolean>>; // calbck to update the expanded state of the card in the parent component
  timePeriod: TimePeriod; // the time period to group the Slack members by
  setTimePeriod?: React.Dispatch<React.SetStateAction<TimePeriod>>; // callback to update in the parent the time period the Slack members are grouped by
  memberType: MemberType; // the type of Slack members to show in the chart
  loadingTotal: boolean; // wether the total members hook is loading or not
  loadingNew: boolean; //  wether the new members hook is loading or not
};

export default function Chart({
  platform,
  axisDisplay,
  totalMembersDay,
  totalMembersWeek,
  totalMembersMonth,
  newMembersDay,
  newMembersWeek,
  newMembersMonth,
  handleMemberType,
  menu,
  popupContent,
  expandMembersOverTime,
  setExpandMembersOverTime,
  timePeriod,
  memberType,
  loadingTotal,
  loadingNew,
}: ChartProps) {
  return (
    <Card
      style={{ marginBottom: 40 }}
      title="Members over time"
      bordered={false}
      extra={
        <Space>
          <p style={{ marginTop: 10 }}>Group by</p>
          <Dropdown className="joyride-date" overlay={menu}>
            <Button>
              {timePeriod} <DownOutlined />
            </Button>
          </Dropdown>
          <Button
            onClick={() => setExpandMembersOverTime(!expandMembersOverTime)}
          >
            <div className="-mt-1">
              <ArrowsAltOutlined />
            </div>
          </Button>
          <Popover
            content={popupContent(
              "New or total members over time, grouped by day, week or month."
            )}
            className="ml-3"
          >
            <InfoCircleOutlined />
          </Popover>
        </Space>
      }
    >
      <Meta
        description={
          <>
            <Space
              align="center"
              style={{
                marginBottom: "20px",
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              <Radio.Group onChange={handleMemberType} value={memberType}>
                <Radio value={MemberType.New}>New members</Radio>
                <Radio value={MemberType.Total}>Total members</Radio>
              </Radio.Group>
            </Space>
          </>
        }
      />
      {platform === "slack" && memberType === MemberType.Total ? (
        <Spin spinning={loadingTotal}>
          <div
            style={{
              width: "100%",
              height: expandMembersOverTime ? "500px" : "250px",
            }}
          >
            <ResponsiveContainer>
              <LineChart
                width={430}
                data={
                  /* eslint-disable indent */
                  timePeriod === TimePeriod.Day
                    ? totalMembersDay
                    : timePeriod === TimePeriod.Week
                    ? totalMembersWeek
                    : timePeriod === TimePeriod.Month
                    ? totalMembersMonth
                    : undefined
                }
                margin={{
                  top: 5,
                  right: 5,
                  left: 0,
                  bottom: 5,
                }}
              >
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis
                  dataKey={axisDisplay()}
                  tickFormatter={(date) => {
                    if (timePeriod === TimePeriod.Month) {
                      return moment(date).format("MM/YYYY");
                    }
                    return moment(date).format("MM/DD");
                  }}
                />
                <YAxis domain={["auto", "auto"]} />
                <Legend />
                <ReTooltip />
                <Line
                  type="monotone"
                  dataKey="total"
                  name="# of total members"
                  stroke="#8884d8"
                  activeDot={{ r: 8 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Spin>
      ) : null}
      {platform === "slack" && memberType === MemberType.New ? (
        <Spin spinning={loadingNew}>
          <div
            style={{
              width: "100%",
              height: expandMembersOverTime ? "500px" : "250px",
            }}
          >
            <ResponsiveContainer>
              <LineChart
                width={430}
                data={
                  /* eslint-disable indent */
                  timePeriod === TimePeriod.Day
                    ? newMembersDay
                    : timePeriod === TimePeriod.Week
                    ? newMembersWeek
                    : timePeriod === TimePeriod.Month
                    ? newMembersMonth
                    : undefined
                }
                margin={{
                  top: 5,
                  right: 5,
                  left: 0,
                  bottom: 5,
                }}
              >
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis
                  dataKey={axisDisplay()}
                  tickFormatter={(date) => {
                    if (timePeriod === TimePeriod.Month) {
                      return moment(date).format("MM/YYYY");
                    }
                    return moment(date).format("MM/DD");
                  }}
                />
                <YAxis domain={["auto", "auto"]} />
                <Legend />
                <ReTooltip />
                <Line
                  type="monotone"
                  dataKey="total"
                  name="# of new members"
                  stroke="#8884d8"
                  activeDot={{ r: 8 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Spin>
      ) : null}
    </Card>
  );
}
